import * as React from "react"

import { AppFunctions } from "@others/AppFunctions.ts"
import { useTranslation } from "react-i18next"
import { AppStreamChatGenerics } from "@interfaces/IAppStreamChatGenerics.ts"
import { Event as StreamEvent, StreamChat } from "stream-chat"

const BACKOFFICE_USER_ID = AppFunctions.getViteEnv(
		"VITE_STREAM_BACKOFFICE_USER_ID"
	),
	FALLBACK_NOTIFICATION_ICON =
		"https://membership.elglobodistrict.com/assets/images/globo_backoffice_avatar.jpg",
	SEEN_MESSAGE_IDS_LIMIT = 100

interface IArgs {
	chatClient?: StreamChat<AppStreamChatGenerics>
	enabled: boolean
}

interface IOutput {
	supported: boolean
	permission?: NotificationPermission
	canRequestPermission: boolean
	isPermissionDenied: boolean
	requestPermission: () => Promise<void>
}

const getNotificationPermission = (): NotificationPermission | undefined => {
	if (typeof Notification === "undefined") return undefined

	return Notification.permission
}

const shouldShowNotification = () => {
	if (typeof document === "undefined") return true

	return document.visibilityState !== "visible" || !document.hasFocus()
}

const useBackofficeChatNotifications = ({
	chatClient,
	enabled,
}: IArgs): IOutput => {
	const { t } = useTranslation()

	const supported = typeof Notification !== "undefined"

	const [permission, setPermission] = React.useState<
		NotificationPermission | undefined
	>(() => getNotificationPermission())

	const seenMessageIdsRef = React.useRef(new Set<string>())
	const seenMessageIdsOrderRef = React.useRef<string[]>([])

	const syncPermission = React.useCallback(() => {
		setPermission(getNotificationPermission())
	}, [])

	const markMessageAsSeen = React.useCallback((messageId: string) => {
		if (seenMessageIdsRef.current.has(messageId)) return false

		seenMessageIdsRef.current.add(messageId)
		seenMessageIdsOrderRef.current.push(messageId)

		if (seenMessageIdsOrderRef.current.length > SEEN_MESSAGE_IDS_LIMIT) {
			const oldestMessageId = seenMessageIdsOrderRef.current.shift()

			if (oldestMessageId) {
				seenMessageIdsRef.current.delete(oldestMessageId)
			}
		}

		return true
	}, [])

	const requestPermission = React.useCallback(async () => {
		if (!supported || Notification.permission !== "default") {
			syncPermission()
			return
		}

		try {
			const nextPermission = await Notification.requestPermission()

			setPermission(nextPermission)
		} catch (error) {
			console.error(
				"Error requesting backoffice chat notification permission:",
				error
			)

			syncPermission()
		}
	}, [supported, syncPermission])

	const handleNewMessage = React.useCallback(
		(event: StreamEvent<AppStreamChatGenerics>) => {
			if (!shouldShowNotification()) return

			const message = event.message
			const messageId = message?.id

			if (!messageId || !markMessageAsSeen(messageId)) return
			if (message.user?.id === BACKOFFICE_USER_ID) return

			const messageText = message.text?.trim()
			const senderName =
				message.user?.name || message.user?.id || "Client"
			const notificationBody = messageText
				? messageText
				: message.attachments?.length
					? t("message_chat_backoffice_notification_attachment_body")
					: t("message_chat_backoffice_notification_generic_body")

			const notification = new Notification(
				t("message_chat_backoffice_notification_title", {
					name: senderName,
				}),
				{
					body: notificationBody,
					icon: message.user?.image || FALLBACK_NOTIFICATION_ICON,
				}
			)

			notification.onclick = () => {
				if (typeof window !== "undefined") {
					window.focus()
				}

				notification.close()
			}
		},
		[markMessageAsSeen, t]
	)

	React.useEffect(() => {
		syncPermission()
	}, [syncPermission])

	React.useEffect(() => {
		if (
			!supported ||
			typeof window === "undefined" ||
			typeof document === "undefined"
		) {
			return
		}

		const handlePermissionStateChange = () => {
			syncPermission()
		}

		window.addEventListener("focus", handlePermissionStateChange)

		document.addEventListener(
			"visibilitychange",
			handlePermissionStateChange
		)

		return () => {
			window.removeEventListener("focus", handlePermissionStateChange)

			document.removeEventListener(
				"visibilitychange",
				handlePermissionStateChange
			)
		}
	}, [supported, syncPermission])

	React.useEffect(() => {
		if (!supported || !chatClient || !enabled || permission !== "granted") {
			return
		}

		chatClient.on("notification.message_new", handleNewMessage)
		chatClient.on("message.new", handleNewMessage)

		return () => {
			chatClient.off("notification.message_new", handleNewMessage)
			chatClient.off("message.new", handleNewMessage)
		}
	}, [chatClient, enabled, handleNewMessage, permission, supported])

	return {
		supported,
		permission,
		canRequestPermission: supported && permission === "default",
		isPermissionDenied: supported && permission === "denied",
		requestPermission,
	}
}

export default useBackofficeChatNotifications
